import React from 'react';
import _ from 'lodash';
import { canUseDOM } from 'exenv';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { limuirsComponent } from 'limuirs-components';
import { log } from 'limuirs-utils';
import SubscriptionStore from './SubscriptionStore';
import { default as FeedbackStore } from '../../common/Feedback/FeedbackStore';
import RestManager from '../../../services/rest/RestManager';
import Raw from '../../common/Raw';

const Modal = loadable(() => import('react-modal'));
const SubscriptionForm = loadable(() => import('./SubscriptionForm'));

class CategorySubscriptionEditor extends React.Component {
  static propTypes = {
    /** Optional override of the link text key used to trigger the subscription editor */
    labelKey: PropTypes.string,
    /** Determines if the node information for the component is lazily loaded, default: true. This is used by the prefetch. */
    /* eslint react/no-unused-prop-types: "off" */
    // used in prefetch freemarker template only
    defer: PropTypes.bool
  };

  static defaultProps = {
    labelKey: null,
    defer: true
  };

  constructor(props) {
    super(props);

    const { globals, prefetch } = this.props;
    this.feedbackStore = FeedbackStore(this.props);
    this.store = new SubscriptionStore(
      new RestManager(globals),
      prefetch,
      this.getBoardLabel,
      this.getCatLabel
    );

    this.state = {
      loadModal: false,
      modalIsOpen: false,
      disableControls: !canUseDOM,
      formMounted: false
    };

    this.editLinkRef = React.createRef();
  }

  openEditor = () =>
    this.store.initStoreData().then(() =>
      this.setState({
        loadModal: true,
        disableControls: false,
        modalIsOpen: true
      })
    );

  closeEditor = result => {
    this.setState({ modalIsOpen: false });
    return result;
  };

  /**
   * Emit an event when a subscription change occurs, this can be listened to
   * by components built in legacy systems to do a partial update.
   */
  dispatchUpdateEvent = () => {
    const event = document.createEvent('CustomEvent');
    event.initCustomEvent('LITHIUM:subscriptionUpdate', true, true, {
      type: 'category',
      id: this.store.topCategory.id
    });
    this.editLinkRef.current.dispatchEvent(event);
  };

  handleFormSubmit = (selectedOption, selected) => {
    const { text } = this.props;
    this.setState({
      disableControls: true
    });
    this.store
      .subscribe({ type: selectedOption, selected })
      .then(({ data }) => {
        if (!data || !Object.prototype.hasOwnProperty.call(data, 'items')) {
          return this.closeEditor(true);
        }
        this.dispatchUpdateEvent();
        if ((selectedOption === 'partial' && data.size > 0) || selectedOption === 'all') {
          this.feedbackStore.addSuccess(text.format('subscribeResult.success', this.store.counts));
          return this.closeEditor(true);
        } else {
          this.feedbackStore.addSuccess(text.format('unsubscribeResult.success'));
          return this.closeEditor(true);
        }
      })
      .catch(error => {
        if (error.status === 403) {
          this.feedbackStore.addInfo(text.format('loggedOut'));
          return this.closeEditor(false);
        } else {
          // this should catch any bugs with the close action (including feedback not working).
          log.warn('caught error handling subscribe action', error);
          this.feedbackStore.addError(text.format('saveError'));
          return this.closeEditor(false);
        }
      });
  };

  getBoardLabel = board => {
    const { id, conversation_style: conversationStyle, title } = board;
    return (
      <span>
        <i
          key={id}
          className={`lia-img-icon-${conversationStyle}-board lia-fa-icon lia-fa-${conversationStyle} lia-fa-board lia-fa`}
          title={conversationStyle}
          aria-label={conversationStyle}
          role="img"
        />
        {title}
      </span>
    );
  };

  getCatLabel = (cat, boardCount) => {
    const { text } = this.props;
    const { title, id } = cat;
    return (
      <span className="lia-category-title">
        <i
          key={id}
          className="lia-img-icon-category lia-fa-icon lia-fa-category lia-fa lia-node-icon"
          title={text.format('category')}
          aria-label={text.format('category')}
          role="img"
        />
        {text.format('boardsInCategoryCount', { categoryTitle: title, boardCount })}
      </span>
    );
  };

  renderModal() {
    const {
      topCategory,
      isTopCatSubscribed,
      hasPartialSubscription,
      checkedNodeIds,
      tree,
      counts
    } = this.store;
    const { text } = this.props;
    const { formMounted, modalIsOpen, disableControls } = this.state;
    const titleKey = isTopCatSubscribed ? 'title' : 'titleSubscribe';
    const title = text.format(titleKey, { topCategoryTitle: topCategory.title });
    const overlayClassName = formMounted ? 'lia-react-modal-overlay' : 'lia-js-hidden';

    return (
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={this.closeEditor}
        className="lia-modal-container lia-cat-sub-editor-modal"
        overlayClassName={overlayClassName}
        contentLabel={title}
        appElement={document.body}
      >
        <div className="lia-modal-content">
          <h2>{title}</h2>
          <a href="javascript:void(0);" onClick={this.closeEditor}>
            <i className="lia-fa lia-fa-times lia-close-modal" />
          </a>

          <SubscriptionForm
            disabled={disableControls}
            text={text}
            submit={this.handleFormSubmit}
            cancel={this.closeEditor}
            newSubscription={!isTopCatSubscribed}
            initialSelectedOption={isTopCatSubscribed && hasPartialSubscription ? 'partial' : 'all'}
            initialChecked={checkedNodeIds}
            boardCounts={counts}
            checkboxTreeNodes={_.get(tree, 'children', [])}
            onMount={() => this.setState({ formMounted: true })}
          />
        </div>
      </Modal>
    );
  }

  render() {
    const { labelKey, text } = this.props;
    const { isTopCatSubscribed, counts, storeDataReady, allowed } = this.store;
    let actionLabelKey;
    if (labelKey !== null) {
      actionLabelKey = labelKey;
    } else {
      actionLabelKey = isTopCatSubscribed ? 'editSubscription' : 'subscribe';
    }

    const actionLabel = text.format(actionLabelKey, counts);

    if (!allowed) {
      return (
        <React.Fragment>
          <span className="lia-menu-item lia-link-navigation lia-link-disabled lia-category-subscription-link">
            <Raw html={actionLabel} />
          </span>
        </React.Fragment>
      );
    }
    const { loadModal } = this.state;
    const hoverOverAction = () => Modal.prefetch() && SubscriptionForm.prefetch();

    return (
      <React.Fragment>
        <a
          href="javascript:void(0);"
          className="lia-link-navigation lia-category-subscription-link"
          onMouseOver={hoverOverAction}
          onFocus={hoverOverAction}
          onClick={this.openEditor}
          ref={this.editLinkRef}
        >
          <Raw html={actionLabel} />
        </a>
        {loadModal && storeDataReady && this.renderModal()}
      </React.Fragment>
    );
  }
}

export default limuirsComponent(module, CategorySubscriptionEditor);
